import React from 'react';
import { string } from 'prop-types';
import { intlShape } from 'react-intl';
import classNames from 'classnames';
import { path, pathOr } from 'ramda';

import { propTypes } from '../../../util/types';
import {
  txIsAccepted,
  txIsPaymentExpired,
  txIsPaymentPending,
  txIsPreAccepted,
} from '../../../util/transactions';
import { createSlug } from '../../../util/urlHelpers';
import { formatMoney } from '../../../util/currency';
import { NamedLink, ResponsiveImage } from '../../../components';

import BookingInfo from '../BookingInfo/BookingInfo';
import ContactActions from '../ContactActions/ContactActions';
import css from './ReservationCard.module.css';

const ReservationCard = (props) => {
  const { className, intl, transaction, type } = props;
  const listing = path(['listing'], transaction);
  const isOrder = type === 'order';
  const showPrivateInfo = transaction && txIsAccepted(transaction);
  const isPreAccepted = transaction && txIsPreAccepted(transaction);
  const isPaymentPending = transaction && txIsPaymentPending(transaction);
  const isPaymentExpired = transaction && txIsPaymentExpired(transaction);
  const listingUuid = pathOr('', ['id', 'uuid'], listing);
  const listingTitle = pathOr('', ['attributes', 'title'], listing);
  const slug = createSlug(listingTitle);
  const listingImg = listing.images && listing.images.length > 0 ? listing.images[0] : null;
  const bookingPrice = isOrder
    ? transaction.attributes.payinTotal
    : transaction.attributes.payoutTotal;
  const price = formatMoney(intl, bookingPrice, false);
  const priceLabel = intl.formatMessage({ id: 'ReservationsPage.ReservationCard.priceLabel' });
  const ctaLabel = intl.formatMessage({
    id: isPaymentExpired
      ? 'ReservationsPage.ReservationCard.preAcceptCtaRetry'
      : 'ReservationsPage.ReservationCard.preAcceptCtaComplete',
  });
  const statusAriaLabel = intl.formatMessage({
    id: 'ReservationsPage.ReservationCard.preAcceptAriaLabel',
  });
  const preAcceptStatus = isPreAccepted
    ? {
        badgeLabel: intl.formatMessage({ id: 'ReservationsPage.ReservationCard.preAcceptBadge' }),
        title: intl.formatMessage({ id: 'ReservationsPage.ReservationCard.preAcceptTitle' }),
        description: intl.formatMessage({
          id: isPaymentExpired
            ? 'ReservationsPage.ReservationCard.preAcceptPaymentExpired'
            : isPaymentPending
            ? 'ReservationsPage.ReservationCard.preAcceptPaymentPending'
            : 'ReservationsPage.ReservationCard.preAcceptPaymentActionRequired',
        }),
        ownerWarning: intl.formatMessage({ id: 'ReservationsPage.ReservationCard.ownerWarning' }),
      }
    : null;
  const classes = classNames(css.root, className, {
    [css.rootPreAccept]: isPreAccepted,
  });

  return (
    <div className={classes}>
      <div className={css.mainContent}>
        {preAcceptStatus ? (
          <div
            className={css.statusBanner}
            role="status"
            aria-live="polite"
            aria-label={statusAriaLabel}
          >
            <div className={css.statusBadge}>{preAcceptStatus.badgeLabel}</div>
            <div className={css.statusTextContainer}>
              <span className={css.statusTitle}>{preAcceptStatus.title}</span>
              {!isOrder && (
                <span className={css.ownerWarning}>
                  {preAcceptStatus.ownerWarning}
                </span>
              )}
              {isOrder ? (
                <NamedLink
                  className={css.statusCta}
                  name="OrderDetailsPage"
                  params={{ id: transaction.id.uuid }}
                >
                  {ctaLabel}
                </NamedLink>
              ) : null}
            </div>
          </div>
        ) : null}
        <BookingInfo
          className={css.bookingInfo}
          intl={intl}
          isOrder={isOrder}
          tx={transaction}
          withPrice={!isOrder}
        />
        <ContactActions
          className={css.contactActions}
          intl={intl}
          isOrder={isOrder}
          showPrivateInfo={showPrivateInfo}
          transaction={transaction}
        />
      </div>
      {isOrder ? (
        <NamedLink
          className={css.listingLink}
          name="ListingPage"
          params={{ id: listingUuid, slug }}
        >
          <ResponsiveImage
            className={css.listingImage}
            alt={'listingImg'}
            image={listingImg}
            variants={['landscape-crop']}
          />
        </NamedLink>
      ) : (
        <div className={css.priceContainer}>
          <div className={css.priceWrapper}>
            <span className={css.priceLabel}>{priceLabel}</span>
            <span className={css.price}>{price}</span>
          </div>
        </div>
      )}
    </div>
  );
};

ReservationCard.defaultProps = {
  className: null,
};

ReservationCard.propTypes = {
  className: string,
  intl: intlShape.isRequired,
  transaction: propTypes.transaction,
  type: string.isRequired,
};

export default ReservationCard;
